import 'requestidlecallback-polyfill';
import ol from '@thoughtspot/openlayers';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography/typography';
import React, { useEffect, useRef, useState } from 'react';
import { getTranslatedTextCallback as t } from '../../env';
import { createLogger } from '../../utils/logger';
import { ALLOWED_WORLD_EXTENT, CUSTOM_REGION_DEFAULT_LABEL_KEY, DATA_PROJECTION, DEFAULT_CENTER, DEFAULT_MAX_REGION_NAMES_TO_LIST, DEFAULT_MIN_ZOOM, DEFAULT_ZOOM, GEOMETRY_TYPE, GEOMETRY_TYPE_PROTO, MAX_CUSTOM_REGION_PREVIEW_REGIONS, VIEW_PROJECTION, } from '../base/geo-constants';
import { GeoUtils } from '../base/geo-utils';
export const DATA_TEST_ID = {
    PREVIEW_LOADER: 'previewLoader',
    REGION_NAME: 'regionName',
};
export const CustomRegionPreview = ({ customRegionContent, onErrorCallback, updateRegionsCount, updateGeometryType, }) => {
    const logger = createLogger('custom-region-preview');
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    let currentMapResizeIdleCallback = null;
    let currentPreviewUpdateIdleCallback = null;
    const layerGroup = new ol.layer.Group();
    const clearIdleCallback = () => {
        if (currentMapResizeIdleCallback)
            window.cancelIdleCallback(currentMapResizeIdleCallback);
        if (currentPreviewUpdateIdleCallback)
            window.cancelIdleCallback(currentPreviewUpdateIdleCallback);
    };
    const restrictCenterExtent = ol.proj.transformExtent(ALLOWED_WORLD_EXTENT, DATA_PROJECTION, VIEW_PROJECTION);
    const view = new ol.View({
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        minZoom: DEFAULT_MIN_ZOOM,
        projection: VIEW_PROJECTION,
    });
    const vectorSource = new ol.source.Vector({});
    const vectorLayer = new ol.layer.Vector({});
    const labelLayer = new ol.layer.Vector({
        declutter: true,
        className: 'label-layer',
        style(feature) {
            const style = GeoUtils.customRegionPreviewStyles.labelStyle;
            style
                .getText()
                .setText(feature.get(CUSTOM_REGION_DEFAULT_LABEL_KEY));
            return [style];
        },
    });
    const getFeatures = () => {
        const topojsonContent = JSON.parse(customRegionContent);
        try {
            return GeoUtils.getCustomFeatures(topojsonContent);
        }
        catch (error) {
            onErrorCallback(t()('customRegion.file.local.error'));
            logger.warn(error);
            setLoading(false);
            return [];
        }
    };
    const filterRegionsFromFeatures = (features) => {
        const regionNames = [];
        const featuresLength = features.length;
        for (let index = 0; index < featuresLength; index++) {
            const regionName = features[index].get(CUSTOM_REGION_DEFAULT_LABEL_KEY);
            if (regionName)
                regionNames.push(regionName);
        }
        return regionNames;
    };
    const fitMapToViewport = () => {
        if (!map.current)
            return;
        map.current.updateSize();
        map.current
            .getView()
            .fit(vectorSource.getExtent(), map.current.getSize());
        const newZoom = map.current.getView().getZoom();
        map.current.getView().setZoom(newZoom);
    };
    const updatePreview = () => {
        const features = getFeatures();
        if (!(features === null || features === void 0 ? void 0 : features.length)) {
            onErrorCallback(t()('customRegion.file.zero.features'));
            setLoading(false);
            return;
        }
        const regionNames = filterRegionsFromFeatures(features);
        const propertyMissingErrorMsg = t()('customRegion.file.feature.property.missing');
        const unSupportedGeometryErrorMsg = t()('customRegion.file.feature.geoMetry.type.notSupported');
        if (regionNames.length === 0) {
            onErrorCallback(propertyMissingErrorMsg.replace('{propertyKey}', CUSTOM_REGION_DEFAULT_LABEL_KEY));
            setLoading(false);
            return;
        }
        const featureType = features[0].getGeometry().getType();
        if ((featureType === null || featureType === void 0 ? void 0 : featureType.toUpperCase()) === GEOMETRY_TYPE.POINT.toUpperCase()) {
            vectorLayer.setStyle(() => {
                return [GeoUtils.customRegionPreviewStyles.pointStyle];
            });
        }
        else if ((featureType === null || featureType === void 0 ? void 0 : featureType.toUpperCase()) ===
            GEOMETRY_TYPE.POLYGON.toUpperCase() ||
            (featureType === null || featureType === void 0 ? void 0 : featureType.toUpperCase()) ===
                GEOMETRY_TYPE.MULTI_POLYGON.toUpperCase()) {
            vectorLayer.setStyle(() => {
                return [GeoUtils.customRegionPreviewStyles.polygonStyle];
            });
        }
        else {
            onErrorCallback(unSupportedGeometryErrorMsg.replace('{geometryType}', featureType));
            setLoading(false);
            return;
        }
        vectorSource.addFeatures(features.slice(0, MAX_CUSTOM_REGION_PREVIEW_REGIONS));
        view.fit(vectorSource.getExtent());
        vectorLayer.setSource(vectorSource);
        labelLayer.setSource(vectorSource);
        layerGroup.getLayers().extend([vectorLayer, labelLayer]);
        updateRegionsCount(regionNames.length);
        updateGeometryType(GEOMETRY_TYPE_PROTO[featureType === null || featureType === void 0 ? void 0 : featureType.toUpperCase()]);
        setRegions(regionNames);
        clearIdleCallback();
        currentMapResizeIdleCallback = window.requestIdleCallback(() => {
            setLoading(false);
            fitMapToViewport();
        });
    };
    useEffect(() => {
        currentPreviewUpdateIdleCallback = window.requestIdleCallback(() => {
            updatePreview();
        });
        return clearIdleCallback;
    }, [customRegionContent]);
    const onDestroy = () => {
        if (map.current) {
            map.current.setTarget(null);
            map.current = null;
        }
        clearIdleCallback();
    };
    useEffect(() => {
        if (mapContainer.current && !map.current) {
            map.current = new ol.Map({
                layers: [layerGroup],
                target: 'map',
                view,
            });
        }
        return onDestroy;
    }, [customRegionContent]);
    const renderRegionNames = React.useCallback(() => {
        const regionElements = [];
        const regionsLength = (regions === null || regions === void 0 ? void 0 : regions.length) || 0;
        if (regionsLength < 1)
            return null;
        const maxRegionsToList = Math.min(regionsLength, DEFAULT_MAX_REGION_NAMES_TO_LIST);
        for (let index = 0; index < maxRegionsToList; index++) {
            regionElements.push(React.createElement(Vertical, { spacing: "d", key: `${regions[index]}-${index}`, "data-testid": DATA_TEST_ID.REGION_NAME },
                React.createElement(Typography, { variant: "p" }, regions[index])));
        }
        if (regionsLength > DEFAULT_MAX_REGION_NAMES_TO_LIST)
            regionElements.push(React.createElement(Vertical, { spacing: "d", key: "region-name-more" },
                React.createElement(Typography, { variant: "p" }, `${regionsLength -
                    DEFAULT_MAX_REGION_NAMES_TO_LIST} ${t()('More')}`)));
        return regionElements;
    }, [regions]);
    return (React.createElement(Horizontal, { className: "previewContainer" },
        loading && (React.createElement(LoadingIndicator.Global, { className: "previewContainer customRegionMapLoading" },
            React.createElement("div", { "data-testid": DATA_TEST_ID.PREVIEW_LOADER }))),
        React.createElement(Vertical, { className: `labelListContainer ${loading ? 'hidden' : ''}` },
            React.createElement(Typography, { variant: "p-bold", noMargin: true }, t()('customRegion.feature.list.name.header').replace('{regionsLength}', String(regions === null || regions === void 0 ? void 0 : regions.length))),
            renderRegionNames()),
        React.createElement(Vertical, { className: `mapContainer ${loading ? 'hidden' : ''}` },
            React.createElement("div", { ref: mapContainer, id: "map", className: "map" }))));
};
export default CustomRegionPreview;
//# sourceMappingURL=custom-region.js.map